import axios from '@/lib/axios'
import { useAuthStore } from '@/stores/auth'
import { useCompanyBlockStore } from '@/stores/companyBlock'
import { useQuery, useQueryClient } from '@tanstack/vue-query'
import { AxiosError } from 'axios'
import { ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { useFeatureFlags } from '@/composables/useFeatureFlags'

import { Ability } from '@/interfaces/ability'
import { SidebarAction } from '@/interfaces/sidebarAction'

import { AuthenticatedResponse } from '@/modules/auth/interfaces/authenticatedResponse'
import { User } from '@/modules/users/interfaces/user'

const getUser = async () => {
	const { data } = await axios.get<AuthenticatedResponse>('/api/user')

	return data
}

export function useAuth({
	middleware,
	redirectIfAuthenticated,
}: { middleware?: string; redirectIfAuthenticated?: string } = {}) {
	const queryClient = useQueryClient()
	const router = useRouter()
	const route = useRoute()
	const authStore = useAuthStore()
	const { setAbilities } = useAuthStore()
	const companyBlock = useCompanyBlockStore()
	const isLoggingIn = ref<boolean>(false)
	const loggingError = ref<string>()
	const isSuccess = ref<boolean>(false)

	const user = ref<User>()
	const abilities = ref<Ability[]>([])
	// Una sola fuente de verdad para los interruptores de módulos (ver useFeatureFlags).
	const { flightsEnabled, carsEnabled, busesEnabled } = useFeatureFlags()

	const sidebarActions = ref<SidebarAction[]>([
		{
			name: 'Inicio',
			ability: 'viewAny_dashboard',
			href: '/dashboard',
			icon: 'HomeIcon',
			permission: [''],
			entityType: [''],
		},
		{
			name: 'Reservar',
			ability: 'viewAny_service',
			icon: 'BriefcaseIcon',
			permission: ['create'],
			entityType: ['App\\Models\\Request'],
			subItems: [
				{
					name: 'Hoteles',
					ability: 'viewAny_service',
					href: '/services/hotel',
					icon: 'hotel',
					permission: ['create-hotel'],
					entityType: ['App\\Models\\Request'],
				},
				...(flightsEnabled ?
					[
						{
							name: 'Aviones',
							ability: 'viewAny_invoice',
							href: '/services/plane',
							icon: 'travel',
							permission: ['create-plane'],
							entityType: ['App\\Models\\Request'],
						},
					]
				:	[]),
				...(carsEnabled ?
					[
						{
							name: 'Autos',
							ability: 'viewAny_invoice',
							href: '/services/car',
							icon: 'directions_car',
							permission: ['create-car'],
							entityType: ['App\\Models\\Request'],
						},
					]
				:	[]),
				...(busesEnabled ?
					[
						{
							name: 'Autobuses',
							ability: 'viewAny_invoice',
							href: '/services/bus',
							// `directions_bus`, no `airport_shuttle`: es el icono que declara el
							// registry (`src/config/services.ts`) y el que usan los otros diez
							// sitios que pintan autobús.
							icon: 'directions_bus',
							permission: ['create-bus'],
							entityType: ['App\\Models\\Request'],
						},
					]
				:	[]),
			],
		},
		{
			name: 'Usuarios',
			ability: 'View users companies',
			icon: 'UserIcon',
			permission: ['view-users'],
			entityType: ['App\\Models\\Company'],
			subItems: [
				{
					name: 'Gestión de usuarios',
					ability: 'View users companies',
					href: '/users',
					icon: 'group',
					permission: ['view-users'],
					entityType: ['App\\Models\\Company'],
				},
				// Solicitudes de ALTA de usuario, no las aprobaciones de reserva del
				// item "Solicitudes pendientes" (ese sigue aparte). Es el mismo destino
				// y el mismo permiso que el botón "Pendientes" de la pantalla de
				// usuarios; su contador lo pinta AppSidebar.
				{
					name: 'Solicitudes pendientes',
					ability: 'viewAny_approval',
					href: '/users/approvals',
					icon: 'how_to_reg',
					permission: ['view', 'viewAny'],
					entityType: ['App\\Models\\Approval', 'App\\Models\\Approval'],
				},
			],
		},
		{
			name: 'Mi empresa',
			internalName: 'Empresas',
			ability: 'viewAny_company',
			href: '/companies',
			icon: 'BuildingOfficeIcon',
			permission: [''],
			entityType: [''],
		},
		{
			name: 'Solicitudes pendientes',
			ability: 'viewAny_approval',
			href: '/pending-requests',
			icon: 'ClipboardDocumentCheckIcon',
			// viewAny:Approval → Master / Travel Manager / Supervisor (no Traveler).
			permission: ['viewAny'],
			entityType: ['App\\Models\\Approval'],
		},
		{
			name: 'Mis itinerarios',
			internalName: 'Solicitudes',
			ability: 'View cefs companies',
			href: '/trips',
			icon: 'TicketIcon',
			permission: ['view-cefs', 'viewAny-internal'],
			entityType: ['App\\Models\\Company', 'App\\Models\\Cef'],
		},
		// Acceso directo al histórico de reservas de vuelo (cancelar/reembolso/cambio).
		// Antes la lista era inalcanzable desde el menú (solo se llegaba desde un detalle).
		...(flightsEnabled ?
			[
				{
					name: 'Mis vuelos',
					ability: 'viewAny_invoice',
					href: '/services/plane/reservations',
					icon: 'PaperAirplaneIcon',
					permission: ['create-plane'],
					entityType: ['App\\Models\\Request'],
				} satisfies SidebarAction,
			]
		:	[]),
		{
			name: 'Reportes',
			accessFromChildren: true,
			ability: 'viewAny_report',
			icon: 'PresentationChartLineIcon',
			permission: [
				'view-hotel-reports-local',
				'view-hotel-reports-legacy',
				'view-ai-reports',
				'view-cefs',
				'viewAny-internal',
			],
			entityType: [
				'App\\Models\\Company',
				'App\\Models\\Company',
				'App\\Models\\Company',
				'App\\Models\\Company',
				'App\\Models\\Cef',
			],
			subItems: [
				{
					name: 'Hotelería · Booking nuevo',
					hotelReport: 'local',
					ability: 'viewAny_report',
					href: '/reports/hotels',
					icon: 'hotel',
					permission: ['view-hotel-reports-local'],
					entityType: ['App\\Models\\Company'],
				},
				{
					name: 'Hotelería · Legacy 2.0',
					hotelReport: 'legacy',
					ability: 'viewAny_report',
					href: '/reports/hotels/legacy',
					icon: 'hotel',
					permission: ['view-hotel-reports-legacy'],
					entityType: ['App\\Models\\Company'],
				},
				// Se les había quedado sin gatear: el reporte de vuelos seguía visible
				// aunque el módulo estuviera apagado, a diferencia de las otras dos
				// superficies de vuelo (Reservar > Aviones y Mis vuelos).
				...(flightsEnabled ?
					[
						{
							name: 'Aviones',
							ability: 'viewAny_report',
							href: '/reports/planes',
							icon: 'flight',
							permission: ['view-cefs', 'viewAny-internal'],
							entityType: ['App\\Models\\Company', 'App\\Models\\Cef'],
						},
					]
				:	[]),
				{
					name: 'Reporte IA',
					ability: 'view-ai-reports',
					href: '/reports/ai-travel',
					icon: 'auto_awesome',
					permission: ['view-ai-reports'],
					entityType: ['App\\Models\\Company'],
				},
			],
		},

		// Oculto a pedido del cliente (First Cash) hasta que se ajuste la sección
		// de facturas — ruta comentada en routesLoader.ts.
		// {
		// 	name: 'Facturas',
		// 	ability: 'viewAny_invoice',
		// 	href: '/invoices',
		// 	icon: 'DocumentTextIcon',
		// 	permission: ['view-invoice'],
		// 	entityType: ['App\\Models\\Company'],
		// },
		{
			name: 'Solicitud de crédito',
			ability: 'manage-credit-request',
			href: '/credit-requests',
			icon: 'BanknotesIcon',
			// manage-credit-request → Master / Travel Manager (nunca Traveler).
			// La empresa que ya tiene crédito entra igual MIENTRAS tenga expediente
			// que consultar: la pantalla muestra el historial y el backend es quien
			// rechaza abrir una nueva solicitud. El item sólo desaparece cuando no
			// hay nada que ver ni que hacer — la regla vive en `useSidebarActions`.
			permission: ['manage-credit-request'],
			entityType: ['App\\Models\\Company'],
		},
		{
			name: 'Cartas garantía',
			ability: 'view',
			href: '/guarantee-letters',
			icon: 'DocumentTextIcon',
			permission: ['view'],
			entityType: ['App\\Models\\ClientGuaranteeLetter'],
		},
		{
			// Agrupa lo que antes eran 3 items sueltos (Grupos y políticas, Roles,
			// y el plan — que vivía solo como bloque grande en el dashboard) bajo
			// una sola pestaña de configuración (feedback cliente First Cash).
			name: 'Configuración empresa',
			ability: 'viewAny_role',
			icon: 'Cog6ToothIcon',
			permission: [''],
			entityType: [''],
			subItems: [
				{
					name: 'Grupos y políticas',
					ability: 'View groups companies',
					href: '/groups',
					icon: 'group',
					// view-groups → Master / Travel Manager (gestión de políticas).
					permission: ['view-groups'],
					entityType: ['App\\Models\\Company'],
				},
				{
					name: 'Roles',
					ability: 'viewAny_role',
					href: '/roles',
					icon: 'key',
					permission: ['view-roles'],
					entityType: ['App\\Models\\Company'],
				},
				{
					name: 'Mi Plan',
					ability: 'viewAny_dashboard',
					href: '/subscription',
					icon: 'workspace_premium',
					permission: [''],
					entityType: [''],
				},
			],
		},
	])

	const routesExternal = ref<SidebarAction[]>([
		// {
		// 	name: 'Cartas responsivas',
		// 	ability: '',
		// 	href: '/responsive-letters',
		// 	icon: 'NewspaperIcon',
		// 	permission: [''],
		// 	entityType: [''],
		// },
	])

	const routesInternal = ref<SidebarAction[]>([])

	/**
	 * Dudas y aclaraciones (teléfonos, correos y horarios de la agencia) ya no es un
	 * elemento del menú: vive como tarjeta fija en el pie de `AppSidebar`, junto a la
	 * empresa y el usuario, porque no es navegación del día a día sino un «llama si
	 * algo no cuadra». El guard de `/contact` sigue mandando a los internos al
	 * dashboard, así que la tarjeta solo se pinta para externos.
	 *
	 * El href se conserva aquí para purgar la entrada de menús ya persistidos en el
	 * store de sesiones anteriores.
	 */
	const CONTACT_HREF = '/contact'

	const { data, isError, refetch, isLoading } = useQuery(['user'], getUser, {
		retry: false,
		refetchOnWindowFocus: true,
	})

	watch(
		data,
		(value) => {
			if (data) {
				user.value = data.value?.user
				abilities.value = data.value?.abilities!

				// /api/user respondió 200 → la empresa NO está apagada (o el bloqueo
				// se resolvió en el backoffice): limpiamos cualquier estado previo.
				if (user.value) {
					companyBlock.reset()
				}

				if (!user.value?.internal) {
					sidebarActions.value = sidebarActions.value ?? []
				}

				sidebarActions.value = sidebarActions.value ?? []
			}
		},
		{
			immediate: true,
		}
	)

	const csrf = () => axios.get('/sanctum/csrf-cookie')

	const login = async ({ ...props }) => {
		isLoggingIn.value = true
		try {
			await csrf()
			await axios.post('/login', props)

			isSuccess.value = true
			refetch()
		} catch (err: AxiosError | any) {
			loggingError.value = err.response.data.message
			throw err
		} finally {
			isLoggingIn.value = false
		}
	}

	const logout = async () => {
		if (!isError.value) {
			await axios.post('/logout').then(() => refetch())
			await queryClient.getQueryCache().clear()
			window.Echo.leaveAllChannels()
		}

		window.location.pathname = '/auth/login'
	}

	const fillAuthStore = (
		user: User,
		abilities: Ability[],
		sidebarAction: SidebarAction[]
	) => {
		if (user && sidebarAction) {
			authStore.setUser(user)
			authStore.setSidebarActions(sidebarAction)
			authStore.setAbilities(abilities)
		}
	}

	watch(
		[user, abilities, isError],
		async ([userValue, abilitiesUser, isErrorValue]) => {
			if (middleware === 'guest' && redirectIfAuthenticated && userValue)
				router.push({ name: 'dashboard' })

			if (
				route.path === '/verify-email' &&
				userValue?.emailVerifiedAt &&
				redirectIfAuthenticated
			)
				router.push({ name: 'dashboard' })

			// No forzar logout si el error es por empresa bloqueada (423): en ese
			// caso mandamos a la pantalla de suspensión, que controla el conteo de
			// 15s y ELLA hace el logout. Sin esta guarda, el usuario cliente saldría
			// directo a /auth/login sin ver el motivo.
			if (middleware === 'auth' && isErrorValue && !companyBlock.blocked) {
				await logout()
			}

			if (userValue?.internal) {
				sidebarActions.value = [
					...sidebarActions.value,
					...routesInternal.value,
				].map((action) => {
					// "Grupos y políticas" vive ahora dentro de "Configuración empresa"
					// (antes era un item suelto); para internos se sigue ocultando,
					// solo que ahora hay que quitarlo de los subItems del grupo.
					if (action.name === 'Configuración empresa' && action.subItems) {
						return {
							...action,
							subItems: action.subItems.filter(
								(sub) => sub.name !== 'Grupos y políticas'
							),
						}
					}
					return action
				})
			} else {
				// ⚠️ Este watcher ACUMULA: se reasigna a partir de `sidebarActions.value`,
				// así que todo lo que se agregue aquí se vuelve a agregar en cada corrida.
				// Y corre al menos dos veces —`immediate: true` con `user` en `undefined`,
				// y otra cuando `/api/user` resuelve—, más una por cada refetch (foco de
				// ventana, navegación). Como `useAuth()` se llama desde una veintena de
				// componentes, el número de copias dependía del orden de montaje.
				//
				// Hoy está latente porque `routesExternal` está vacío, pero quien lo
				// descomente tiene que filtrar sus propios hrefs igual que abajo: el
				// `v-for` del sidebar keyea por `item.name` y dos filas con el mismo
				// nombre chocan la llave.
				//
				// El filtro de `/contact` limpia el menú guardado por versiones previas,
				// cuando «Dudas y aclaraciones» todavía era un elemento de navegación.
				sidebarActions.value = [
					...sidebarActions.value.filter(
						(action) => action.href !== CONTACT_HREF
					),
					...routesExternal.value,
				]
			}

			fillAuthStore(userValue!, abilitiesUser, sidebarActions.value)
		},
		{ immediate: true }
	)

	const hasPermissionUser = (title: string, entityType: string): boolean => {
		if (!title) return true

		if (abilities.value?.some((ability) => ability?.name === '*')) return true

		if (entityType) {
			return abilities.value.some(
				(ability) => ability.name === title && ability.entityType === entityType
			)
		} else {
			return abilities.value.some((ability) => ability.name === title)
		}
	}

	return {
		user,
		abilities,
		isLoading,
		isLoggingIn,
		isSuccess,
		loggingError,

		login,
		logout,
		refetch,
		hasPermissionUser,
	}
}
